import React from 'react';
import AdminReportsSection from '../components/reports/AdminReportsSection';
import { useCanAccessPrivacyKpis } from '../hooks/useHasRole';
import { FiShield } from 'react-icons/fi';

const SettingsAndReportsPage: React.FC = () => {
  const canAccessPrivacyKpis = useCanAccessPrivacyKpis();

  const navigateToPrivacyKpis = () => {
    window.history.pushState({}, '', '?view=admin/privacy-kpis');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Removed Header component - now using GlassHeader from App.tsx */}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-slate-800 mb-6">Reports</h1>

        {/* Admin-Only Section: Privacy KPIs (PRIV-25.2) */}
        {canAccessPrivacyKpis && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiShield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-blue-900">Privacy & Security KPIs</h2>
                  <p className="text-sm text-blue-700">Monitor compliance metrics, DSAR requests, and privacy operations</p>
                </div>
              </div>
              <button
                onClick={navigateToPrivacyKpis}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FiShield className="w-4 h-4" />
                Open Dashboard
              </button>
            </div>
            <p className="text-xs text-blue-600 mt-3">
              🔒 Admin/Auditor access only • PRIV-25.2 Compliance Dashboard
            </p>
          </div>
        )}

        {/* Admin Reports Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <AdminReportsSection />
        </div>

        {/* Reports Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <ReportsContent />
        </div>
      </main>
    </div>
  );
};

// Reports content component
const ReportsContent: React.FC = () => {
  return (
    <div>
      <h2 className="text-xl font-semibold mb-4 text-slate-800">Generate Reports</h2>

      <div className="space-y-6">
        {/* Report Types */}
        <section>
          <h3 className="text-lg font-medium mb-3 text-slate-700">Available Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Member Performance Report */}
            <div className="border border-slate-200 rounded-md p-4 hover:bg-slate-50 cursor-pointer">
              <h4 className="font-medium mb-2 text-slate-700">Member Performance</h4>
              <p className="text-sm text-slate-500 mb-3">
                Analyze member performance metrics over time
              </p>
              <button className="px-3 py-1 bg-brand-blue text-white text-sm rounded-md hover:bg-brand-blue/90">
                Generate
              </button>
            </div>

            {/* Financial Health Report */}
            <div className="border border-slate-200 rounded-md p-4 hover:bg-slate-50 cursor-pointer">
              <h4 className="font-medium mb-2 text-slate-700">Financial Health Summary</h4>
              <p className="text-sm text-slate-500 mb-3">
                Overview of financial health metrics across all members
              </p>
              <button className="px-3 py-1 bg-brand-blue text-white text-sm rounded-md hover:bg-brand-blue/90">
                Generate
              </button>
            </div>

            {/* Onboarding Status Report */}
            <div className="border border-slate-200 rounded-md p-4 hover:bg-slate-50 cursor-pointer">
              <h4 className="font-medium mb-2 text-slate-700">Onboarding Status</h4>
              <p className="text-sm text-slate-500 mb-3">
                Track member onboarding progress and completion rates
              </p>
              <button className="px-3 py-1 bg-brand-blue text-white text-sm rounded-md hover:bg-brand-blue/90">
                Generate
              </button>
            </div>

            {/* Risk Assessment Report */}
            <div className="border border-slate-200 rounded-md p-4 hover:bg-slate-50 cursor-pointer">
              <h4 className="font-medium mb-2 text-slate-700">Risk Assessment</h4>
              <p className="text-sm text-slate-500 mb-3">
                Identify high-risk members and potential intervention points
              </p>
              <button className="px-3 py-1 bg-brand-blue text-white text-sm rounded-md hover:bg-brand-blue/90">
                Generate
              </button>
            </div>
          </div>
        </section>

        {/* Custom Report Builder */}
        <section>
          <h3 className="text-lg font-medium mb-3 text-slate-700">Custom Report Builder</h3>
          <div className="bg-slate-50 p-4 rounded-md">
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Report Name</label>
              <input
                type="text"
                className="w-full p-2 border border-slate-300 rounded-md"
                placeholder="My Custom Report"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Select Metrics
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center">
                  <input type="checkbox" id="metric1" className="mr-2" />
                  <label htmlFor="metric1" className="text-slate-700">
                    VeraScore
                  </label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="metric2" className="mr-2" />
                  <label htmlFor="metric2" className="text-slate-700">
                    Credit Score
                  </label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="metric3" className="mr-2" />
                  <label htmlFor="metric3" className="text-slate-700">
                    Debt to Income
                  </label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="metric4" className="mr-2" />
                  <label htmlFor="metric4" className="text-slate-700">
                    Cash on Hand
                  </label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="metric5" className="mr-2" />
                  <label htmlFor="metric5" className="text-slate-700">
                    Payment History
                  </label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="metric6" className="mr-2" />
                  <label htmlFor="metric6" className="text-slate-700">
                    Savings Rate
                  </label>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Date Range</label>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <input type="date" className="p-2 border border-slate-300 rounded-md w-full sm:w-auto" />
                <span className="self-center text-slate-600">to</span>
                <input type="date" className="p-2 border border-slate-300 rounded-md w-full sm:w-auto" />
              </div>
            </div>

            <div className="mt-4">
              <button className="px-4 py-2 bg-brand-blue text-white rounded-md hover:bg-brand-blue/90">
                Build Custom Report
              </button>
            </div>
          </div>
        </section>

        {/* Recent Reports */}
        <section>
          <h3 className="text-lg font-medium mb-3 text-slate-700">Recent Reports</h3>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Report Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date Generated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    Q1 Member Performance
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">2023-03-15</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    Performance
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    <button className="text-brand-blue hover:text-brand-blue/80">Download</button>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    Financial Health Overview
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">2023-02-28</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">Financial</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    <button className="text-brand-blue hover:text-brand-blue/80">Download</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsAndReportsPage;
